/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { LinkRoute } from './links';

import usePortal from '../../helpers/usePortal';

import { getList, markRead, markAllRead } from '../../../../services/endpoints/notification';

const portalStyles = (mobile, windowSize) => `
  display: block;
  position: fixed;
  top: ${mobile ? '56px' : '68px'};
  right: ${mobile ? '0' : '16px'};
  left: ${mobile ? '0' : 'auto'};
  width: ${mobile ? 'auto' : '320px'};
  height: ${mobile ? `${windowSize?.height || 56 - 56}px` : 'auto'};
  z-index: 1050;
`;

const Notification = ({ component, socketsInit, updateNoti, setUpdateNoti }) => {
  const btnNode = useRef(null);
  const bodyNode = useRef(null);

  const [items, setItems] = useState([]);
  const [listOffset, setListOffset] = useState(0);
  const [isReadAllowed, setIsReadAllowed] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPart, setIsLoadingPart] = useState(false);
  const [isLoadingBody, setIsLoadingBody] = useState(false);
  const [unreadCount, setUnreadCount] = useState(null);
  const [isEmptyList, setIsEmptyList] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [unreaded, setUnreaded] = useState(false);

  const { openPortal, closePortal, isOpen, Portal } = usePortal({ portalStyles, closeOnOutsideClick: true });

  const onClick = (event) => {
    event.preventDefault();
    isOpen ? closePortal(event) : openPortal(event);
  };

  const getData = async (init) => {
    setIsLoading(true);

    try {
      const response = await getList({ offset: 0 });

      if (response?.success) {
        const { items, counter, is_read_allowed } = response.data;
        setIsReadAllowed(is_read_allowed);
        setItems(items);
        setUnreadCount(counter);
      } else {
        setDisabled(true);
        setUnreaded(false);
        setUnreadCount(null);
      }
      setIsLoading(false);
    } catch (error) {
      setDisabled(true);
      setUnreaded(false);
      setUnreadCount(null);
      !init && setIsLoading(false);
    }
  };

  const sendMarkRead = async (id) => {
    setUpdateNoti(true);
    try {
      await markRead(id);
    } catch (error) {
      console.log(error);
    }
  };

  const sendMarkAllRead = async () => {
    try {
      await markAllRead();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getData(true);
  }, []);

  useEffect(() => {
    if (!socketsInit) {
      if (isOpen) {
        getData();
      } else {
        setListOffset(0);
        setIsLoading(true);
      }
    }
  }, [isOpen, socketsInit]);

  useEffect(() => {
    if (items?.length > 0) {
      setDisabled(false);
      setIsEmptyList(false);

      if (items.some((item) => !item.read_at)) {
        setUnreaded(true);
      } else {
        setUnreaded(false);
      }
    } else {
      setIsEmptyList(true);
      setDisabled(true);
      setUnreaded(false);
      setUnreadCount(null);
    }
  }, [items]);

  useEffect(() => {
    if (updateNoti && !isOpen) {
      getData();
      setUpdateNoti(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoti, isOpen]);

  const onRead = (item) => {
    if (!item.read_at) {
      sendMarkRead(item.id);
      setItems((prev) => prev.map((noti) => {
        if (noti.id === item.id) {
          return {
            ...noti,
            read_at: true,
          };
        }

        return noti;
      }));
      setUnreadCount((prev) => prev - 1);
    }
  };

  const onReadAll = async () => {
    setUnreadCount(0);
    setIsLoadingBody(true);

    try {
      await sendMarkAllRead();
      const response = await getList({ offset: 0 });

      if (response?.success) {
        const { items, counter } = response.data;
        setItems(items);
        setUnreadCount(counter);
        setListOffset(0);
        bodyNode?.current?.scrollTo({
          top: 0,
          left: 0,
        });
      }
      setIsLoadingBody(false);
    } catch (error) {
      setIsLoadingBody(false);
    }
  };

  const handleScroll = async (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    const scroll = scrollHeight - scrollTop - clientHeight;

    const newOffset = listOffset + 10;
    if (newOffset > unreadCount || isLoadingPart) {
      return;
    }

    if (Math.floor(scroll) <= 0) {
      setListOffset(newOffset);
      setIsLoadingPart(true);

      try {
        const response = await getList({ offset: newOffset });

        if (response?.success) {
          const { items, counter } = response.data;
          setItems((prev) => [...prev, ...items]);
          setUnreadCount(counter);
        }
        setIsLoadingPart(false);
      } catch (error) {
        setIsLoadingPart(false);
      }
    }
  };

  return (
    <>
      <a
        href="# "
        role="button"
        className={`nav__link nav__link-icon nav__link-notification navbar-popover__toggle${disabled ? ' disabled' : ''}${
          unreaded ? ' unreaded' : ''
        }${isOpen ? ' opened' : ''}`}
        onClick={onClick}
        ref={btnNode}
      >
        <i className="fas fa-bell" />
        <span className={`unreaded-badge${unreaded ? ' showed' : ''}`}>
          <span className="unreaded-badge-content" />
        </span>
      </a>

      {isOpen && (
        <Portal>
          <div className="navbar-popover">
            <div className="navbar-popover__header">
              <div className="title">
                <span>Notifications</span>
                {!isLoading && unreadCount && unreadCount > 0 ? <span className="badge badge-secondary">{unreadCount}</span> : null}
              </div>
              <button type="button" className="close" onClick={closePortal}>
                <span aria-hidden="true">×</span>
                <span className="sr-only">Close</span>
              </button>
            </div>
            {isEmptyList && !isLoading ? (
              <div className="navbar-popover__body-empty">
                <i className="fas fa-bell icon" />
                <div className="title">No notifications yet</div>
                <div className="desc">New notifications will appear here.</div>
              </div>
            ) : (
              <div className={`navbar-popover__body${isLoadingBody ? ' navbar-popover__body-loader-abs' : ''}`}>
                <ul className="body-list" onScroll={handleScroll} ref={bodyNode}>
                  {isLoading ? (
                    <li className="body-list__item-loader" />
                  ) : (
                    items.map((item) => (
                      <li key={item.id} className={`body-list__item${item.read_at ? '' : ' unread'}`} onClick={() => isReadAllowed && onRead(item)}>
                        <div className="title">{item.title}</div>
                        <div className="content" dangerouslySetInnerHTML={{ __html: item.description }} />
                        <div className="date">{item.created_at}</div>
                        {item?.action && (
                          <LinkRoute component={component} to={item.action.url} className="btn btn-xs btn-default">
                            <i className="fas fa-arrow-right" />
                          </LinkRoute>
                        )}
                      </li>
                    ))
                  )}
                  {isLoadingPart && <li className="body-list__item-loader" />}
                </ul>
              </div>
            )}

            {!isLoading && isReadAllowed && !isEmptyList ? (
              <div className="navbar-popover__footer">
                <button type="button" className={`btn btn-link${!unreaded ? ' disabled' : ''}`} onClick={unreaded ? onReadAll : undefined}>
                  Mark all as read
                </button>
              </div>
            ) : null}
          </div>
        </Portal>
      )}
    </>
  );
};

Notification.propTypes = {
  component: PropTypes.any,
  socketsInit: PropTypes.bool,
  updateNoti: PropTypes.bool,
  setUpdateNoti: PropTypes.func,
};

export default Notification;
